import React, { useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { Row, Col, FormGroup, Label, Input, Button, Card, CardBody, Table, Nav, NavItem, NavLink, UncontrolledDropdown, CardHeader, CardFooter } from "reactstrap";
import { useEtim } from "../../context/ETIMContext";
import { useService } from "../../context/ServiceContext";
import styles from "./EtimCharacteristics.module.css";
import { isEmpty } from "lodash";

export default function EtimCharacteristics(props) {
  const { isModal, handleCharacteristicsNext } = props;
  const history = useHistory();
  const { id, classEtim } = isModal ? props : props.match.params;
  const { etim, setEtim, products, setIsDirty } = useEtim();
  const [selectedGroup, setSelectedGroup] = useState();
  const [features, setFeatures] = useState([]);
  const { etimProductsProvider, etimGroupedProductsProvider, etimCharacteristicsProvider } = useService();
  const isMountedRef = useRef(false);
  const intl = useIntl();

  useEffect(() => {
    if (etim.classEtim == null || etim.classEtim == "") {
      if (classEtim) {
        setEtim({ type: "SET_SERIES_ID", payload: id });
        setEtim({ type: "SET_CLASS_ETIM", payload: classEtim });
      } else {
        history.push(`/series/etim/edit/${etim.seriesId}`);
      }
    }
  }, []);

  const processFeatures = (data) => {
    const groupedFeatures = data.reduce((acc, char) => {
      if (!acc[char.featureId]) {
        acc[char.featureId] = {
          ...char,
          options: [], // for dropdown feature type A
        };
      } else {
        acc[char.featureId].options = [...new Set([...acc[char.featureId].options, ...[{ id: char.valueId, name: char.valueDescription }]])];
      }
      return acc;
    }, {});

    const features = Object.values(groupedFeatures);

    return features;
  };

  useEffect(() => {
    isMountedRef.current = true;
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        // console.log(`${!isMountedRef.current}`, `${etim.seriesId == null && isEmpty(etim.seriesId)}`, `${isEmpty(etim.version)}`);
        if (!isMountedRef.current || (etim.seriesId == null && isEmpty(etim.seriesId)) || isEmpty(etim.version)) return;
        if (!isMountedRef.current) return;

        if (etim.groupedProducts == null || etim.groupedProducts.length === 0) {
          const existingGroups = await etimGroupedProductsProvider.get({ seriesId: etim.seriesId, version: etim.version }, { signal: abortController.signal });
          setEtim({ type: "SET_EXISTING_ETIM_COMMERICAL_CLASS", payload: existingGroups.map((g) => g.commercialClassETIM) });
          if (existingGroups.length === 1 && (existingGroups[0].commercialClassETIM === "" || existingGroups[0].commercialClassETIM === null)) {
            existingGroups[0].commercialClassETIM = etim.seriesName;
          }
          setEtim({
            type: "ADD_GROUPED_PRODUCT",
            payload: existingGroups.map((g) => ({
              commercialClassETIM: g.commercialClassETIM,
              products: g.products || [],
              parameterValues: g.parameterValues || {},
              new: false,
            })),
          });
        }

        const featuresCharacteristicsAttributesData = await etimCharacteristicsProvider.get(
          { version: etim.version, classETIM: etim.classEtim, seriesId: etim.seriesId },
          { signal: abortController.signal }
        );
        if (!isMountedRef.current) return;
        setFeatures(processFeatures(featuresCharacteristicsAttributesData));
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching data:", error);
      }
    };

    fetchData();

    return () => {
      isMountedRef.current = false;
      abortController.abort(); // Cancel pending requests
    };
  }, [etim.seriesId, etim.version, etim.classEtim, etimProductsProvider, etimGroupedProductsProvider, etimCharacteristicsProvider, setEtim]);

  useEffect(() => {
    if (selectedGroup == null && etim.groupedProducts.length > 0) {
      setSelectedGroup(etim.groupedProducts[0]);
    }
  }, [etim.groupedProducts]);

  const handleCreateGroup = async (cloneParameters) => {
    let newGroup = {
      commercialClassETIM: `group ${(etim.groupedProducts?.length ?? 0) + 1}`,
      products: [],
      parameterValues: cloneParameters ? { ...selectedGroup.parameterValues } : {},
      id: uuidv4(),
      new: true,
    };
    setSelectedGroup(newGroup);
    setEtim({ type: "ADD_GROUPED_PRODUCT", payload: newGroup });

    setIsDirty(true);
  };

  const handleRemoveGroup = async (removeGroup) => {
    setSelectedGroup(null);
    setEtim({ type: "REMOVE_GROUPED_PRODUCT", payload: removeGroup });
    setIsDirty(true);
  };

  const handleSelectGroup = (id) => {
    setSelectedGroup({ ...etim }.groupedProducts.find((g) => g.id === id));
    // setEtim((prev) => ({ ...prev, selectedGroup: commercialClassETIM }));
  };

  const handleProductToggle = (codProducto) => {
    const newProducts = new Set(selectedGroup.products || []);
    if (newProducts.has(codProducto)) newProducts.delete(codProducto);
    else newProducts.add(codProducto);
    let newSelectedGroup = { ...selectedGroup, products: Array.from(newProducts) };
    setSelectedGroup(newSelectedGroup);
    setEtim({ type: "UPDATE_GROUPED_PRODUCTS", payload: newSelectedGroup });
    setIsDirty(true);
  };

  const handleParameterChange = (featureNr, value) => {
    const feature = features.find((f) => f.featureNr == featureNr);
    if (feature) {
      setSelectedGroup((prev) => {
        let newSelectedGroup = {
          ...prev,
          parameterValues: { ...prev.parameterValues, [featureNr]: value },
        };
        setEtim({
          type: "UPDATE_GROUPED_PRODUCTS",
          payload: newSelectedGroup,
        });
        setIsDirty(true);
        return newSelectedGroup;
      });
    }
  };

  const handleFeatureDefined = function (featureNr, isDefined) {
    setFeatures((prevFeatures) => prevFeatures.map((f) => (f.featureNr === featureNr ? { ...f, isDefined: isDefined } : f)));
  };

  const handleCommercialClassEtimChange = (e) => {
    const commercialClassETIM = e.target.value;
    console.log("Input change:", commercialClassETIM);

    setSelectedGroup((prev) => {
      const newSelectedGroup = {
        ...prev,
        commercialClassETIM: commercialClassETIM,
      };

      console.log("Dispatching to reducer:", newSelectedGroup);

      setEtim({
        type: "UPDATE_GROUPED_PRODUCTS",
        payload: newSelectedGroup,
      });

      setIsDirty(true);
      return newSelectedGroup;
    });
  };

  const handleDeleteGroup = (groupId) => {
    setEtim({ type: "REMOVE_GROUPED_PRODUCT", payload: { groupId } });
  };

  const renderInput = (feature) => {
    const value = selectedGroup?.parameterValues[feature.featureNr] || "";

    //isDefined can override isDataFromBdP setting
    const disabled = feature.isDataFromBdP ? !feature.isDefined : !feature.isDefined;
    //return <Input value={value} disabled />;

    switch (feature.featureType) {
      case "N":
        return (
          <Input
            disabled={disabled}
            type="number"
            value={value}
            onChange={(e) => handleParameterChange(feature.featureNr, e.target.value)}
            placeholder={`Enter value (${feature.unitDesc})`}
          />
        );
      case "A":
        return (
          <Input disabled={disabled} type="select" value={value} onChange={(e) => handleParameterChange(feature.featureNr, e.target.value)}>
            <option value="">{intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Select an option" })} </option>
            {feature.options.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Input>
        );
      case "L":
        return (
          <input
            disabled={disabled}
            type="checkbox"
            className="checkbox"
            checked={typeof value === "string" && value.toLowerCase() === "true"}
            onChange={(e) => handleParameterChange(feature.featureNr, e.target.checked.toString())}
          />
        );
      case "R":
        const [min, max] = value.split("-").map(Number) || [null, null];
        return (
          <Row>
            <Col md="6">
              <Input
                disabled={disabled}
                type="number"
                value={min || ""}
                onChange={(e) => handleParameterChange(feature.featureNr, `${e.target.value}-${max || ""}`)}
                placeholder={`Min (${feature.unitDesc})`}
              />
            </Col>
            <Col md="6">
              <Input
                disabled={disabled}
                type="number"
                value={max || ""}
                onChange={(e) => handleParameterChange(feature.featureNr, `${min || ""}-${e.target.value}`)}
                placeholder={`Max (${feature.unitDesc})`}
              />
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  return (
    <div className="etim-characteristics">
      <Card>
        <CardHeader>
          <h1>
            <FormattedMessage id="etim.characteristics_title" defaultMessage="Define ETIM Characteristics" />
          </h1>
        </CardHeader>
        <CardBody>
          <Nav tabs className="pb-2" style={{ overflow: "initial" }}>
            {etim.groupedProducts &&
              etim.groupedProducts.map((group) => (
                <NavItem key={group.id}>
                  <NavLink key={group.id} id={group.id} active={selectedGroup != null ? selectedGroup.id === group.id : false} onClick={() => handleSelectGroup(group.id)} href="#">
                    {group.commercialClassETIM}
                  </NavLink>
                </NavItem>
              ))}
            <NavItem>
              <UncontrolledDropdown group>
                {/* <Input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Enter new group name" className="mt-2" /> */}
                <Button
                  className="btn-outline-primary"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateGroup(false);
                  }}
                  size="sm"
                >
                  <i className="fas fa-plus"></i> <FormattedMessage id="app.add" defaultMessage="Add" />
                </Button>
              </UncontrolledDropdown>
            </NavItem>
          </Nav>
          <div className="p-4">
            {selectedGroup && (
              <div className="d-flex justify-content-between mb-3">
                <FormGroup>
                  <Label for="commercialClassEtim">
                    <FormattedMessage id="attribute.CommercialClassETIM" defaultMessage="Commercial Class ETIM" />
                  </Label>
                  <Input required={true} type="test" id="commercialClassEtim" value={selectedGroup?.commercialClassETIM} onChange={handleCommercialClassEtimChange} />
                </FormGroup>
                <div>
                  <Button
                    color="link"
                    type="button"
                    // disabled={}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateGroup(true);
                    }}
                  >
                    <i className="fas fa-copy"></i> <FormattedMessage id="app.clone" defaultMessage="Clone" />
                  </Button>
                  <Button
                    color="link"
                    type="button"
                    // disabled={}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveGroup(selectedGroup);
                    }}
                  >
                    <i className="fas fa-trash"></i> <FormattedMessage id="app.remove" defaultMessage="Remove" />
                  </Button>
                </div>
              </div>
            )}
            <Row>
              <Col md="5" className={styles["scrollable-section"]}>
                <h4>
                  <FormattedMessage id="etim.selected_group_products" defaultMessage="Products in Selected Group" />
                </h4>
                {selectedGroup && (
                  <Table responsive className={`${styles.table} align-items-center table-borderless`}>
                    <thead className={styles["sticky-header"]}>
                      <tr>
                        <th>
                          <FormattedMessage id="app.select" defaultMessage="Select" />
                        </th>
                        <th>
                          <FormattedMessage id="app.name" defaultMessage="Name" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className={styles["scrollable-body"]}>
                      {products.map((product) => {
                        let included = selectedGroup.products.includes(product.id);
                        let isDisabled = etim.groupedProducts.some((g) => g.commercialClassETIM !== selectedGroup.commercialClassETIM && g.products.includes(product.id));
                        return (
                          <tr key={product.codProductoComercial} style={{ color: isDisabled ? "gray" : "" }}>
                            <td>
                              <input
                                style={{ cursor: "pointer" }}
                                id={product.id}
                                type="checkbox"
                                className="checkbox"
                                checked={included}
                                onChange={() => handleProductToggle(product.id)}
                                disabled={isDisabled}
                              />
                            </td>
                            {/* <td>{product.id}</td> */}
                            <td>
                              <Label style={{ cursor: "pointer" }} for={product.id}>
                                {product.name || "-"}
                              </Label>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
                {!etim.selectedGroup && <p className="text-muted">Select a group to edit products.</p>}
              </Col>
              <Col md="7" className={styles["scrollable-section"]}>
                <div className="d-flex justify-content-between pl-2 pr-2">
                  <h4 style={{ flex: "90%" }}>
                    <FormattedMessage id="etim.parameter_values" defaultMessage="Parameter Values" />
                  </h4>
                  <h4 style={{ flex: "10%" }}>
                    <FormattedMessage id="etim.define" defaultMessage="Define" />
                  </h4>
                </div>

                {selectedGroup && features.length > 0 ? (
                  <div className={`${styles["scrollable-body"]} p-3`}>
                    {features.map((feature) => (
                      <div className="d-flex">
                        <FormGroup key={feature.featureNr} style={{ flex: "90%" }}>
                          <Label className="w-100">
                            {feature.featureDescription} {feature.unitDesc && `(${feature.unitDesc})`}
                          </Label>
                          {renderInput(feature)}
                        </FormGroup>
                        <FormGroup style={{ flex: "10%", display: "flex", "justify-content": "center", marginTop: "20px" }}>
                          <input
                            type="checkbox"
                            className="checkbox float-right"
                            checked={feature.isDefined}
                            onChange={(e) => handleFeatureDefined(feature.featureNr, e.target.checked)}
                          />
                        </FormGroup>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">
                    <FormattedMessage id="etim.select_group_parameters" defaultMessage="Select a group to define parameters." />
                  </p>
                )}
              </Col>
            </Row>
          </div>
        </CardBody>

        <CardFooter>
          <Row className="mt-4">
            <Col md="12" className="text-right">
              <Button color="primary" onClick={handleCharacteristicsNext}>
                <FormattedMessage id="app.next" defaultMessage="Next" />
              </Button>
            </Col>
          </Row>
        </CardFooter>
      </Card>
    </div>
  );
}
